import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import QuotePopup from '@/pages/home/components/QuotePopup';

const photos = [
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/30569f06-4fc4-4772-a901-aa4dff957cf4_Steel-Rebar-Container-Loading-2-1024x768.jpg?v=6977847868c9bf923df43570e75947a4',
    caption: 'Container arrival at Foshan port — verified seal number and container condition before opening',
    orientation: 'landscape',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/b6b9c5b1-eef9-444a-bba8-b555adb2eec0_Steel-Rebar-Container-Loading-4.jpg?v=16b145028a99da6e5ec805ddc85c94a9',
    caption: 'Mill test certificates reviewed on-site — every bundle matched the buyer\'s specification sheet',
    orientation: 'landscape',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/330461ce-a66c-4ad5-b010-0873b379f36f_Steel-Rebar-Container-Loading-7-1024x768.jpg?v=e9090c2268431b467684432ce21ec585',
    caption: 'Physical measurement of rebar diameter and length — confirming compliance with HRB400 standard',
    orientation: 'landscape',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/21e181fa-a2d6-4920-b6e9-dae0d6ec5ca5_Steel-Rebar-Container-Loading-9-1024x768.jpg?v=d5149eeae54e125cf3f4d1ffa4a2cb8f',
    caption: 'Rebar bundles staged and ready — each bundle tagged with heat number for full traceability',
    orientation: 'landscape',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/68a63646-0b23-467d-9d98-852bb8ff13ec_Steel-Rebar-Container-Loading-8-1024x768.jpg?v=695878cdde7298dc1df7006d2e3344a0',
    caption: 'Loading in progress — bundles carefully stacked to maximise container capacity without damage',
    orientation: 'landscape',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/7a5a218e-cd84-44e8-a260-34a6bc804342_Steel-Rebar-Container-Loading-10-1024x768.jpg?v=9f004296a1a88d31e89dde7d84c1efe2',
    caption: 'Full container loaded — 26MT of HRB400 rebar, ready for sealing and customs clearance',
    orientation: 'landscape',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/e1e31230-5784-491b-86e0-8ed87f3a9f74_Steel-Rebar-Container-Loading-15-768x1024.jpg?v=308b240c17237a97a2de222fe7019f1d',
    caption: 'Final inspection of rebar surface quality — checking for cracks, rust, or deformation before sealing',
    orientation: 'portrait',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/89c71f78-1541-4cc7-8181-1e34a19f323b_Steel-Rebar-Container-Loading-13-768x1024.jpg?v=19ee20e462349fecf5ab88109bb2e338',
    caption: 'Container door sealed with official customs seal — documented with photos sent to buyer in real time',
    orientation: 'portrait',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/fc47dbe3-0d5c-4b33-bdf6-67baf290756e_Steel-Rebar-Container-Loading-14-768x1024.jpg?v=956022f0e3bf8baad39b2660c6414ba1',
    caption: 'Shipping documents prepared — Bill of Lading, packing list, and certificate of origin ready for dispatch',
    orientation: 'portrait',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/00d496aa-12e5-4f07-9ea3-3bc68f5a3c6a_Steel-Rebar-Container-Loading-12-768x1024.jpg?v=0ea2c4eb9d78b12bd3f2ee7b2f5a11d4',
    caption: 'Heat number tag on rebar bundle — traceable back to the original mill and production batch',
    orientation: 'portrait',
  },
];

const steps = [
  {
    number: '01',
    title: 'Order Confirmation & Mill Selection',
    description: 'Once the buyer confirms the order and pays the deposit, we select the most suitable certified mill based on the required specification (grade, size, quantity). For this order: HRB400 rebar (high-strength construction grade), 12mm diameter, 26 metric tons, shipped FOB from Guangzhou (meaning the price covers delivery to the Chinese port).',
    icon: 'ri-file-check-line',
  },
  {
    number: '02',
    title: 'Container Arrival & Inspection',
    description: 'We inspect the container before loading begins. This includes checking the container number, looking for any damage, moisture, or contamination inside, and confirming the seal number matches the shipping documents.',
    icon: 'ri-search-eye-line',
    photos: [photos[0]],
  },
  {
    number: '03',
    title: 'Material Verification',
    description: 'Before any steel enters the container, our team physically checks the material against the mill quality certificate. We verify the batch number on each bundle, measure the diameter and length, inspect the surface, and confirm the total weight matches the order.',
    icon: 'ri-scales-3-line',
    photos: [photos[1], photos[2]],
  },
  {
    number: '04',
    title: 'Loading Process',
    description: 'Rebar bundles are loaded carefully to maximise container capacity while preventing damage in transit. Each bundle is tagged and positioned to allow easy unloading at the destination port. We document every stage with photos sent directly to the buyer.',
    icon: 'ri-truck-line',
    photos: [photos[3], photos[4], photos[5]],
  },
  {
    number: '05',
    title: 'Final Inspection & Sealing',
    description: 'Once fully loaded, a final visual check is done before the container doors are closed. We verify the total count, check for any loose material, and confirm the load is secure. The container is then sealed with an official customs seal.',
    icon: 'ri-shield-check-line',
    photos: [photos[6], photos[7]],
  },
  {
    number: '06',
    title: 'Documentation & Shipping',
    description: 'All shipping documents are prepared and sent to the buyer: Bill of Lading (proof of shipment), Commercial Invoice, Packing List, Certificate of Origin (proves where the steel was made), and Mill Quality Certificates. The buyer gets real-time updates and tracking throughout the voyage.',
    icon: 'ri-file-paper-2-line',
    photos: [photos[8], photos[9]],
  },
];

const orderDetails = [
  { label: 'Product', value: 'Steel Rebar HRB400 (High-Strength Grade)' },
  { label: 'Specification', value: '12mm diameter, 9m length' },
  { label: 'Quantity', value: '26 Metric Tons (1 x 40ft container)' },
  { label: 'Origin', value: 'Foshan, Guangdong, China' },
  { label: 'Destination', value: 'West Africa' },
  { label: 'Shipping Terms', value: 'FOB Guangzhou — price includes delivery to Chinese port' },
  { label: 'Lead Time', value: '18 days from deposit payment' },
  { label: 'Certification', value: 'Mill Quality Certificate + SGS Independent Inspection' },
];

export default function SourcingCasePage() {
  const navigate = useNavigate();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden" style={{ backgroundColor: '#0a1929' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #1e5fa8 0%, transparent 60%), radial-gradient(circle at 80% 20%, #0a2342 0%, transparent 50%)' }} />
        <div className="relative max-w-[1100px] mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold mb-6 border border-white/20 text-white/70">
            <i className="ri-map-pin-2-line" />
            Foshan, Guangdong, China
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-5 leading-tight">
            Steel Rebar Container Loading<br />
            <span style={{ color: '#7eb8f7' }}>On-Site Sourcing Case Study</span>
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            A real order, documented step by step. See exactly how we source, inspect, load, and ship steel rebar from China to your port — with full transparency at every stage.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setQuoteOpen(true)}
              className="px-7 py-3.5 text-white font-black text-sm rounded-lg cursor-pointer transition-all whitespace-nowrap"
              style={{ backgroundColor: '#1e5fa8' }}
            >
              <i className="ri-flashlight-line mr-2" />Get a Quote Like This
            </button>
            <a
              href="https://wa.me/8617671262710"
              target="_blank"
              rel="noopener noreferrer"
              className="px-7 py-3.5 bg-green-500 hover:bg-green-600 text-white font-black text-sm rounded-lg cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-whatsapp-line" />Ask About This Product
            </a>
          </div>
        </div>
      </section>

      {/* Order Summary */}
      <section className="py-14 bg-gray-50/60">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-1">
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest mb-3" style={{ color: '#1e5fa8' }}>
                <i className="ri-information-line" />Order Details
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-3">About This Order</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                This case documents a real shipment of HRB400 steel rebar sourced from a certified mill in Foshan, China. The buyer is a construction materials importer based in West Africa. The entire process from order confirmation to container sealing took 18 days.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {orderDetails.map((item) => (
                  <div key={item.label} className="bg-white rounded-xl p-4 border border-gray-100">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">{item.label}</p>
                    <p className="text-sm font-semibold text-gray-800">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest mb-3" style={{ color: '#1e5fa8' }}>
              <i className="ri-list-ordered" />Step-by-Step Process
            </div>
            <h2 className="text-3xl font-black text-gray-900">How We Handle Your Order</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto text-sm">Every shipment follows this exact process. No shortcuts, no surprises — just transparent, documented steel sourcing from China.</p>
          </div>

          <div className="space-y-20">
            {steps.map((step, idx) => (
              <div key={step.number} className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-start`}>
                {/* Text */}
                <div className="lg:w-[42%] flex-shrink-0">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl text-white text-xl flex-shrink-0" style={{ backgroundColor: '#0a2342' }}>
                      <i className={step.icon} />
                    </div>
                    <span className="text-5xl font-black leading-none" style={{ color: '#e8edf2' }}>{step.number}</span>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                </div>

                {/* Photos */}
                {step.photos && step.photos.length > 0 && (
                  <div className={`flex-1 grid gap-3 ${step.photos.length === 1 ? 'grid-cols-1' : step.photos.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
                    {step.photos.map((photo, pIdx) => (
                      <div
                        key={pIdx}
                        className={`relative overflow-hidden rounded-xl cursor-pointer group ${photo.orientation === 'portrait' ? 'aspect-[3/4]' : 'aspect-[4/3]'} ${step.photos!.length === 1 ? 'max-w-lg' : ''}`}
                        onClick={() => setLightbox(photo.url)}
                      >
                        <img
                          src={photo.url}
                          alt={photo.caption}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                            <i className="ri-zoom-in-line text-gray-800" />
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <p className="text-white text-xs leading-snug">{photo.caption}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* No photos — show a stat block */}
                {(!step.photos || step.photos.length === 0) && (
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-time-line', label: 'Lead Time', value: '18 Days' },
                      { icon: 'ri-shield-check-line', label: 'Certified Mills', value: '12+ Partners' },
                      { icon: 'ri-file-list-3-line', label: 'Documents', value: 'Full Set' },
                      { icon: 'ri-global-line', label: 'Destinations', value: '40+ Countries' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg mb-3" style={{ backgroundColor: '#e8f0fb' }}>
                          <i className={`${stat.icon} text-base`} style={{ color: '#1e5fa8' }} />
                        </div>
                        <p className="text-xl font-black text-gray-900">{stat.value}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full photo gallery */}
      <section className="py-16" style={{ backgroundColor: '#f7f9fc' }}>
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest mb-3" style={{ color: '#1e5fa8' }}>
              <i className="ri-image-2-line" />Photo Documentation
            </div>
            <h2 className="text-2xl font-black text-gray-900">All Loading Photos</h2>
            <p className="text-gray-500 mt-2 text-sm">Every shipment is fully documented. Buyers receive a complete photo report at each stage.</p>
          </div>
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 space-y-3">
            {photos.map((photo, idx) => (
              <div
                key={idx}
                className="break-inside-avoid overflow-hidden rounded-xl cursor-pointer group relative"
                onClick={() => setLightbox(photo.url)}
              >
                <img
                  src={photo.url}
                  alt={photo.caption}
                  className="w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end">
                  <div className="p-3 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <p className="text-white text-xs leading-snug">{photo.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest mb-3" style={{ color: '#1e5fa8' }}>
                <i className="ri-question-line" />Why This Matters
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-5">Why On-Site Inspection Changes Everything</h2>
              <div className="space-y-4">
                {[
                  {
                    icon: 'ri-eye-line',
                    title: 'You see what you\'re buying',
                    desc: 'Real photos of your actual goods — not stock images. You know exactly what\'s in the container before it leaves China.',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'No substitution risk',
                    desc: 'Our team physically verifies heat numbers, dimensions, and weight against the mill test certificate. Substitution is impossible.',
                  },
                  {
                    icon: 'ri-file-text-line',
                    title: 'Full documentation trail',
                    desc: 'Every step is documented. If there\'s ever a dispute, you have photographic evidence of the goods at every stage.',
                  },
                  {
                    icon: 'ri-time-line',
                    title: 'Faster dispute resolution',
                    desc: 'Problems are caught before loading, not after arrival. This saves weeks of back-and-forth and protects your project timeline.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5" style={{ backgroundColor: '#e8f0fb' }}>
                      <i className={`${item.icon} text-sm`} style={{ color: '#1e5fa8' }} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm mb-0.5">{item.title}</p>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '100%', label: 'Shipments documented with photos before sealing', icon: 'ri-camera-line' },
                { value: '18 Days', label: 'Average lead time from deposit to container sealed', icon: 'ri-time-line' },
                { value: '12+', label: 'Certified mill partners across Guangdong & Jiangsu', icon: 'ri-building-2-line' },
                { value: '40+', label: 'Countries served with on-time FOB delivery', icon: 'ri-global-line' },
              ].map((stat) => (
                <div key={stat.label} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 flex flex-col gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: '#e8f0fb' }}>
                    <i className={`${stat.icon} text-lg`} style={{ color: '#1e5fa8' }} />
                  </div>
                  <p className="text-3xl font-black" style={{ color: '#0a2342' }}>{stat.value}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ backgroundColor: '#0a2342' }}>
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">Want This Level of Transparency for Your Order?</h2>
          <p className="text-white/60 text-sm mb-8 max-w-lg mx-auto">
            Every order we handle gets the same on-site inspection, photo documentation, and full shipping support. Get a quote today and see the difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setQuoteOpen(true)}
              className="px-8 py-4 bg-white font-black text-sm rounded-lg cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
              style={{ color: '#0a2342' }}
            >
              <i className="ri-file-list-3-line mr-2" />Get a Free Quote
            </button>
            <a
              href="https://wa.me/8617671262710"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-black text-sm rounded-lg cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-whatsapp-line" />Chat on WhatsApp
            </a>
            <button
              onClick={() => navigate('/about')}
              className="px-8 py-4 border-2 border-white/30 hover:border-white/60 text-white font-black text-sm rounded-lg cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-information-line mr-2" />About Us
            </button>
          </div>
        </div>
      </section>

      <FooterSection />

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
            onClick={() => setLightbox(null)}
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={lightbox}
            alt="Full size"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {quoteOpen && <QuotePopup onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}
